<template>
    <section class="import-queue" :aria-busy="busy">
        <label class="d-block mb-2">Месячные пакеты истории
            <b-form-file :value="files" multiple accept="application/json,.json" :disabled="busy || locked"
                :file-name-formatter="selectedFileLabel" browse-text="Выбрать файлы" placeholder="Выберите сразу все месячные JSON-файлы" drop-placeholder="Перетащите файлы сюда" @input="selectFiles" />
        </label>
        <small class="text-muted d-block mb-2">Можно выделить все файлы в converted-corrected. Отчёты conversion-report.json и verification-report.json будут пропущены.</small>
        <b-alert v-if="error" show variant="danger">{{ error }}</b-alert>
        <b-alert v-if="notice" show variant="info">{{ notice }}</b-alert>
        <b-alert v-if="mappingPending" show variant="warning">Сопоставление распространено не на все месяцы. Откройте исходный месяц через «Разобрать» и повторите сопоставление с включённым общим флажком.</b-alert>
        <template v-if="items.length">
            <div class="import-queue__actions">
                <b-button variant="primary" :disabled="busy || locked || hasInvalid || !hasFilesToCheck" @click="previewAll">Проверить все пакеты</b-button>
                <b-button variant="primary" :disabled="busy || locked || !canPrepareObjects" @click="previewObjects">Подготовить объекты всей очереди</b-button>
                <b-button variant="success" :disabled="busy || locked || !canApply" @click="confirmApply">{{ appliedCount ? 'Продолжить перенос' : 'Перенести все месяцы' }}</b-button>
                <b-button v-if="busy" variant="outline-secondary" :disabled="pauseRequested" @click="pauseRequested = true">{{ pauseRequested ? 'Останавливаемся после текущего запроса…' : 'Остановить после текущего запроса' }}</b-button>
                <b-button v-else variant="link" :disabled="locked" @click="refreshAll">Обновить состояние</b-button>
                <b-button variant="link" :disabled="busy || locked" @click="downloadJournal">Скачать журнал</b-button>
                <b-button variant="link" :disabled="busy || locked" @click="clearQueue">Очистить список</b-button>
            </div>
            <section v-if="objectPlan" class="import-queue__objects mt-3 mb-3">
                <h2 class="h5">Объекты всего архива</h2>
                <p class="small mb-2">Совпадают или уже подтверждены: {{ objectPlan.counts.link }} · создать архивными: {{ objectPlan.counts.create }} · требуют проверки: {{ objectPlan.counts.review }}.
                    Одно подтверждение действует на все строки выбранных месяцев. У новых архивных объектов не будет рабочих процессов и назначений.</p>
                <b-form-checkbox :checked="allObjectsSelected" :disabled="busy || locked" @change="selectAllObjects">Выбрать все однозначные решения</b-form-checkbox>
                <details class="mt-2"><summary>Проверить список объектов ({{ objectPlan.groups.length }})</summary>
                    <div v-for="group in objectPlan.groups" :key="group.name" class="import-queue__object">
                        <b-form-checkbox v-model="selectedObjects" :value="group.name" :disabled="busy || locked || group.action === 'review'">
                            {{ group.name }} — {{ objectAction(group) }} · {{ group.row_count }} строк, {{ group.batch_ids.length }} мес.
                        </b-form-checkbox>
                        <small v-if="group.reasons.length" class="text-danger d-block">{{ group.reasons.join(' ') }}</small>
                        <b-button v-if="group.action === 'review'" size="sm" variant="link" :disabled="busy || locked" @click="$emit('open', group.batch_ids[0])">Разобрать отдельно</b-button>
                    </div>
                </details>
                <b-button class="mt-2" variant="success" :disabled="busy || locked || !selectedObjects.length" @click="prepareObjects">Подтвердить выбранные объекты ({{ selectedObjects.length }})</b-button>
            </section>
            <b-form-checkbox v-model="pauseAfterFirst" class="mt-2" :disabled="busy || locked">Пауза после первого нового месяца для сверки</b-form-checkbox>
            <p class="small text-muted mt-1 mb-2">Сначала последний месяц, затем остальные по хронологии. Каждый месяц записывается целиком. После ошибки следующие месяцы не запускаются. Во время работы оставьте вкладку открытой.</p>
            <p class="mb-1" role="status">{{ progressText }} · перенесено {{ appliedCount }} из {{ items.length }} · готовы {{ readyCount }} · требуют внимания {{ problemCount }}</p>
            <b-progress :value="appliedCount" :max="items.length" variant="success" class="mb-2" />
            <div class="table-responsive">
                <table class="table table-sm mb-0">
                    <thead><tr><th>Месяц / файл</th><th>Состав</th><th>Итог начислений</th><th>Состояние</th><th><span class="sr-only">Подробности</span></th></tr></thead>
                    <tbody><tr v-for="item in items" :key="item.key" :class="{'table-danger': ['error', 'invalid', 'blocked'].includes(item.state)}">
                        <td><strong>{{ item.period ? item.period.slice(0, 7) : '—' }}</strong><small class="d-block text-muted">{{ item.name }}</small></td>
                        <td class="small">{{ composition(item) }}</td>
                        <td class="small"><span v-for="total in totals(item)" :key="total.currency" class="d-block">{{ money(total.amount, total.currency) }}</span></td>
                        <td><span>{{ stateLabel(item.state) }}</span><small v-if="item.message" class="d-block">{{ item.message }}</small><small v-if="item.warningCount" class="d-block text-muted">Предупреждений: {{ item.warningCount }}</small></td>
                        <td><b-button v-if="item.batchId" size="sm" variant="outline-secondary" :disabled="busy || locked" @click="$emit('open', item.batchId)">Разобрать</b-button></td>
                    </tr></tbody>
                </table>
            </div>
        </template>
    </section>
</template>

<script>
import api from '@/utils/api';
import {createSingleFileFormData, uploadFileWithRetry, toFileArray} from '@/utils/fileUploadQueue';

const reportFiles = ['conversion-report.json', 'verification-report.json'];
const blockingRows = rows => (rows || []).filter(row => (row.record_type === 'timesheet_line' && !row.building_id)
    || (row.record_type === 'compensation_term' && !row.employee_id));

export default {
    name: 'TimesheetImportQueue',
    props: {locked: Boolean},
    data() { return {files: [], items: [], busy: false, phase: '', pauseRequested: false, pauseAfterFirst: true,
        checkpointDone: false, mappingPending: false, objectPlan: null, selectedObjects: [], runKey: '', disposed: false, error: '', notice: '', currentName: ''}; },
    computed: {
        journalKey() {
            const user = this.$store.getters.currentUser || {};
            return user.company && user.userId ? `argus:history-import:v1:${user.company}:${user.userId}` : '';
        },
        appliedCount() { return this.items.filter(item => item.state === 'applied').length; },
        readyCount() { return this.items.filter(item => item.state === 'ready').length; },
        problemCount() { return this.items.filter(item => ['blocked', 'error', 'invalid', 'missing'].includes(item.state)).length; },
        hasInvalid() { return this.items.some(item => item.state === 'invalid'); },
        canPrepareObjects() { return this.items.some(item => item.state !== 'applied') && this.items.every(item => item.batchId); },
        allObjectsSelected() { return !!this.objectPlan && this.selectedObjects.length > 0
            && this.selectedObjects.length === this.objectPlan.groups.filter(group => group.action !== 'review').length; },
        hasFilesToCheck() { return this.items.some(item => item.file && item.state !== 'applied'); },
        canApply() { return !this.mappingPending && this.items.some(item => item.state !== 'applied') && this.items.every(item =>
            item.batchId && ['ready', 'applied', 'retry'].includes(item.state)); },
        progressText() { return this.busy ? `${this.phase}: ${this.currentName || '…'}`
            : this.items.length && this.appliedCount === this.items.length ? 'Перенос завершён' : 'Очередь остановлена'; }
    },
    watch: {
        journalKey() {
            if (this.busy) this.pauseRequested = true;
            else { this.items = []; this.objectPlan = null; this.selectedObjects = []; this.restore(); }
        }
    },
    mounted() { window.addEventListener('beforeunload', this.beforeUnload); this.restore(); },
    beforeDestroy() { this.disposed = true; this.pauseRequested = true; window.removeEventListener('beforeunload', this.beforeUnload); },
    methods: {
        objectAction(group) { return group.action === 'create' ? 'создать архивный объект'
            : group.action === 'review' ? 'нужна проверка' : `связать с «${group.target_name}» (№${group.building_id})`; },
        selectAllObjects(value) { this.selectedObjects = value ? this.objectPlan.groups.filter(group => group.action !== 'review').map(group => group.name) : []; },
        async previewObjects() {
            if (this.busy || this.locked || !this.canPrepareObjects) return;
            await this.run('Проверяем объекты всего архива', async () => {
                this.objectPlan = null; this.selectedObjects = []; this.ensureContext();
                const {data} = await api.post('/api/timesheets/imports/objects/preview', {batch_ids: this.items.map(item => item.batchId)}, {ignoreModal: true});
                this.ensureContext(); this.objectPlan = data; this.selectAllObjects(true);
            });
        },
        async prepareObjects() {
            if (this.busy || this.locked || !this.objectPlan || !this.selectedObjects.length) return;
            await this.run('Подтверждаем объекты', async () => {
                const selected = this.objectPlan.groups.filter(group => this.selectedObjects.includes(group.name));
                const count = selected.filter(group => group.action === 'create').length;
                const confirmed = await this.$bvModal.msgBoxConfirm(`Подтвердить ${selected.length} объектов для всех выбранных месяцев? Будет создано архивных объектов: ${count}. Табели и начисления переносятся следующим действием.`, {
                    title: 'Подготовка объектов архива', okTitle: 'Подтвердить объекты', cancelTitle: 'Отмена', okVariant: 'success'});
                if (!confirmed || this.pauseRequested) return;
                this.ensureContext();
                const payload = {batch_ids: this.items.map(item => item.batchId), review_token: this.objectPlan.review_token, names: this.selectedObjects.slice()};
                // Never retry a mutation on a lost response. A new plan reads the committed mappings.
                this.objectPlan = null; this.selectedObjects = [];
                const {data} = await api.post('/api/timesheets/imports/objects/prepare', payload, {ignoreModal: true});
                this.ensureContext(); this.$emit('clear');
                for (const item of this.items) {
                    if (this.pauseRequested) break;
                    await this.readItem(item);
                }
                this.notice = `Объекты подготовлены: связано ${data.linked}, создано архивных ${data.created}, исходных строк ${data.rows}.`;
            });
        },
        selectedFileLabel(files) { return `Выбрано файлов: ${files.length}`; },
        message(error) {
            if (error && !error.response && (error.message === 'Network Error' || error.code === 'ECONNABORTED')) {
                return 'Ответ сервера не получен. Файлы и сопоставления сохранены. Обновите состояние очереди перед продолжением.';
            }
            return error && error.response && error.response.data && error.response.data.msg || error.message || 'Не удалось выполнить запрос.';
        },
        stateLabel(state) { return {waiting: 'Ждёт проверки', reading: 'Читаем файл', checking: 'Проверяем', ready: 'Готов',
            blocked: 'Есть блокировки', applying: 'Переносим', applied: 'Перенесён', error: 'Ошибка', invalid: 'Неверный пакет',
            missing: 'Нужен исходный файл', retry: 'Нужно повторить запрос'}[state] || state; },
        composition(item) { const c = item.counts || {}; return `Табели: ${c.timesheet_line || 0} · начисления: ${c.payroll_line || 0} · оценки: ${c.historical_assessment || 0}${c.historical_payroll_input ? ` · без решения: ${c.historical_payroll_input}` : ''}`; },
        totals(item) { return Object.keys(item.totals || {}).map(currency => ({currency, amount: item.totals[currency].final_amount_minor})); },
        money(amount, currency) { return new Intl.NumberFormat('ru-RU', {style: 'currency', currency}).format(Number(amount) / 100); },
        beforeUnload(event) { if (this.busy) { event.preventDefault(); event.returnValue = ''; } },
        ensureContext() {
            if (this.disposed) throw new Error('Очередь закрыта.');
            if (this.runKey !== this.journalKey) throw new Error('Компания или пользователь изменились. Заново откройте очередь импорта.');
        },
        persist() {
            if (!this.journalKey || (this.runKey && this.runKey !== this.journalKey)) return;
            try { sessionStorage.setItem(this.journalKey, JSON.stringify({checkpointDone: this.checkpointDone,
                pauseAfterFirst: this.pauseAfterFirst, mappingPending: this.mappingPending, items: this.items.map(item => ({key: item.key, name: item.name,
                    period: item.period, batchId: item.batchId, sourceSystem: item.sourceSystem}))})); }
            catch (_) { this.notice = 'Не удалось сохранить очередь в этой вкладке. После обновления выберите файлы снова: уже перенесённые месяцы не дублируются.'; }
        },
        async run(phase, action) {
            if (this.busy) return;
            this.busy = true; this.$emit('busy', true); this.runKey = this.journalKey;
            this.phase = phase; this.pauseRequested = false; this.error = ''; this.notice = '';
            try { await action(); if (this.pauseRequested) this.notice = 'Очередь остановлена. Завершённые месяцы сохранены; можно продолжить.'; }
            catch (error) { this.error = this.message(error); }
            finally {
                this.persist();
                if (this.runKey !== this.journalKey) { this.items = []; this.objectPlan = null; this.selectedObjects = []; }
                this.runKey = ''; this.currentName = ''; this.busy = false; this.$emit('busy', false);
            }
        },
        async selectFiles(files) {
            if (this.busy || this.locked) return;
            const selected = toFileArray(files);
            if (!selected.length) return;
            await this.run('Читаем файлы', async () => {
                const skipped = [], nextItems = [];
                for (const file of selected) {
                    this.ensureContext();
                    if (this.pauseRequested) return;
                    if (reportFiles.includes(file.name)) { skipped.push(file.name); continue; }
                    const item = {key: String(nextItems.length), name: file.name, file: null, period: '', batchId: null,
                        state: 'reading', message: '', counts: {}, totals: {}, warningCount: 0, sourceSystem: ''};
                    nextItems.push(item); this.currentName = file.name;
                    try {
                        if (file.size > 100 * 1024 * 1024) throw new Error('Размер файла больше 100 МБ.');
                        // Same immutable file snapshot as the 1C importer: do not upload disk-backed Files later.
                        const bytes = await file.arrayBuffer();
                        this.ensureContext();
                        item.file = new File([bytes], file.name, {type: file.type || 'application/json', lastModified: file.lastModified});
                        const value = JSON.parse(await item.file.text());
                        if (value.schema_version !== 1 || !value.manifest || value.manifest.complete_month !== true
                            || !/^\d{4}-(0[1-9]|1[0-2])-01$/.test(value.manifest.period_start) || !Array.isArray(value.records)) {
                            throw new Error('Нужен полный месячный пакет JSON v1 с manifest.');
                        }
                        const user = this.$store.getters.currentUser || {};
                        if (value.company !== user.company) throw new Error('Пакет относится к другой компании.');
                        item.period = value.manifest.period_start; item.sourceSystem = value.source_system;
                        item.counts = value.manifest.record_counts || {}; item.state = 'waiting';
                    } catch (error) { throw new Error(`${file.name}: ${this.message(error)} Предыдущая очередь сохранена.`); }
                }
                const months = new Map();
                nextItems.forEach(item => {
                    if (!item.period) return;
                    if (months.has(item.period)) {
                        throw new Error(`${item.period.slice(0, 7)}: выбрано несколько файлов одного месяца. Оставьте один полный пакет. Предыдущая очередь сохранена.`);
                    } else months.set(item.period, item);
                });
                this.ensureContext();
                if (this.pauseRequested) return;
                if (!nextItems.length) throw new Error('Выбраны только отчёты. Выберите месячные файлы YYYY-MM.json.');
                nextItems.sort((a, b) => a.period.localeCompare(b.period));
                if (nextItems.length > 1) nextItems.unshift(nextItems.pop());
                this.items = nextItems; this.files = nextItems.map(item => item.file);
                this.objectPlan = null; this.selectedObjects = [];
                this.checkpointDone = false; this.$emit('clear');
                if (skipped.length) this.notice = `Пропущены отчёты: ${skipped.join(', ')}.`;
            });
        },
        accept(item, payload) {
            const batch = payload.batch || {}, summary = payload.summary || batch.summary || {};
            item.batchId = batch.id; item.period = batch.manifest && batch.manifest.period_start || item.period;
            item.sourceSystem = batch.source_system; item.counts = summary.by_type || item.counts;
            item.totals = summary.totals_by_currency || {}; item.message = '';
            if (Array.isArray(payload.rows)) {
                // Keep a compact source index, not all payroll/source rows for every month.
                const links = new Map();
                payload.rows.forEach(row => ['object', 'employee'].forEach(type => {
                    const value = row.normalized_payload && row.normalized_payload[type];
                    if (!value || !value.source_key) return;
                    const link = {type, key: value.source_key, label: value.name || value.full_name};
                    links.set(JSON.stringify(link), link);
                }));
                item.sourceLinks = Array.from(links.values());
            }
            const diagnostics = summary.diagnostics || {};
            item.warningCount = (diagnostics.warnings || batch.warnings || []).length;
            const blocks = diagnostics.blocking || [];
            const unresolved = blockingRows(payload.rows);
            item.diagnostics = {blocking: blocks, warnings: diagnostics.warnings || batch.warnings || [],
                unresolved_objects: summary.unresolved_objects || 0, unresolved_employees: summary.unresolved_employees || 0};
            item.state = batch.status === 'applied' ? 'applied' : blocks.length || unresolved.length ? 'blocked' : 'ready';
            if (item.state === 'blocked') item.message = blocks.length ? `${blocks.length} ошибок проверки. Откройте подробности.`
                : `Нужно сопоставить ${unresolved.length} исходных строк. Откройте подробности.`;
            this.persist();
        },
        async readItem(item) {
            this.ensureContext(); this.currentName = item.name;
            const {data} = await api.get(`/api/timesheets/imports/${item.batchId}`, {ignoreModal: true});
            this.ensureContext(); this.accept(item, data); return data;
        },
        async previewAll() {
            if (this.locked || this.hasInvalid) return;
            await this.run('Проверяем', async () => {
                for (const item of this.items) {
                    if (this.pauseRequested) break;
                    this.ensureContext();
                    if (item.state === 'applied') continue;
                    try {
                        if (item.batchId) { await this.readItem(item); continue; }
                        if (!item.file) throw new Error('Выберите исходные файлы повторно.');
                        this.currentName = item.name; item.state = 'checking';
                        const {data} = await uploadFileWithRetry(file => {
                            this.ensureContext();
                            return api.post('/api/timesheets/imports/preview', createSingleFileFormData('file', file), {
                                ignoreModal: true, headers: {'Content-Type': 'multipart/form-data'}});
                        }, item.file, {shouldRetry: error => {
                            if (this.disposed || this.pauseRequested || this.runKey !== this.journalKey) return false;
                            if (!error.response) return error.message === 'Network Error' || error.code === 'ECONNABORTED';
                            return [408, 429, 502, 503, 504].includes(Number(error.response.status));
                        }});
                        this.ensureContext(); this.accept(item, data);
                    } catch (error) { item.state = 'error'; item.message = this.message(error); throw error; }
                }
            });
        },
        async refreshAll() {
            if (this.locked) return;
            await this.run('Обновляем состояние', async () => {
                for (const item of this.items) {
                    if (this.pauseRequested) break;
                    if (item.batchId) {
                        try { await this.readItem(item); }
                        catch (error) { item.state = 'error'; item.message = this.message(error); throw error; }
                    }
                }
            });
        },
        async confirmApply() {
            if (this.busy || this.locked || !this.canApply) return;
            // Lock selection before opening confirmation, including route changes.
            await this.run('Подтверждение переноса', async () => {
                const count = this.items.filter(item => item.state !== 'applied').length;
                const confirmed = await this.$bvModal.msgBoxConfirm(`Перенести ${count} мес.? Каждый месяц станет закрытой историей. ${this.pauseAfterFirst && !this.checkpointDone ? 'После первого нового месяца будет пауза для сверки.' : ''}`, {
                    title: 'Пакетный перенос истории', okTitle: 'Начать перенос', cancelTitle: 'Отмена', okVariant: 'success'});
                if (!confirmed || this.pauseRequested) return;
                this.ensureContext(); this.$emit('clear'); this.phase = 'Переносим';
                for (const item of this.items) {
                    if (this.pauseRequested) break;
                    if (item.state === 'applied') continue;
                    try {
                        await this.readItem(item);
                        if (this.pauseRequested) break;
                        if (item.state === 'applied') continue;
                        if (item.state !== 'ready') throw new Error(`${item.name}: сначала устраните блокировки.`);
                        this.ensureContext(); item.state = 'applying'; this.persist();
                        const {data} = await api.post(`/api/timesheets/imports/${item.batchId}/apply`, {}, {ignoreModal: true});
                        this.ensureContext(); this.accept(item, data);
                        if (item.state !== 'applied') throw new Error('Сервер не подтвердил завершение месяца. Обновите состояние.');
                        if (this.pauseAfterFirst && !this.checkpointDone && !data.already_applied) {
                            this.checkpointDone = true;
                            this.notice = `${item.period.slice(0, 7)} перенесён. Сверьте результат, затем нажмите «Продолжить перенос».`;
                            break;
                        }
                    } catch (error) {
                        // The response may have been lost after commit. GET on retry decides the actual state.
                        if (item.state === 'applying') item.state = 'retry';
                        item.message = this.message(error); throw error;
                    }
                }
            });
        },
        async restore() {
            if (!this.journalKey || this.busy) return;
            try {
                const saved = JSON.parse(sessionStorage.getItem(this.journalKey) || 'null');
                if (!saved || !Array.isArray(saved.items)) return;
                this.checkpointDone = !!saved.checkpointDone; this.pauseAfterFirst = saved.pauseAfterFirst !== false;
                this.mappingPending = !!saved.mappingPending;
                this.items = saved.items.map(item => ({...item, file: null, state: item.batchId ? 'waiting' : 'missing',
                    message: item.batchId ? '' : 'Повторно выберите исходные файлы.', counts: {}, totals: {}, warningCount: 0}));
                await this.refreshAll();
            } catch (_) { this.error = 'Не удалось восстановить очередь. Выберите файлы снова; уже перенесённые месяцы не дублируются.'; }
        },
        async spreadMapping(payload, sourceBatchId) {
            let failure = null;
            await this.run('Сопоставляем месяцы', async () => {
                try {
                    const source = this.items.find(item => Number(item.batchId) === Number(sourceBatchId));
                    if (!source) return;
                    this.mappingPending = true; this.persist();
                    for (const item of this.items) {
                        if (this.pauseRequested) throw new Error('Распространение сопоставления остановлено. Повторите его перед переносом.');
                        if (!item.batchId || item.state === 'applied' || item.sourceSystem !== source.sourceSystem) continue;
                        if (item.sourceLinks && !item.sourceLinks.some(link => link.type === payload.mapping_type && link.key === payload.source_key)) continue;
                        const data = await this.readItem(item);
                        if (this.pauseRequested) throw new Error('Распространение сопоставления остановлено. Повторите его перед переносом.');
                        if (item.state === 'applied') continue;
                        const key = payload.mapping_type === 'object' ? 'object' : 'employee';
                        const matches = data.rows.filter(row => row.normalized_payload && row.normalized_payload[key]
                            && row.normalized_payload[key].source_key === payload.source_key);
                        if (!matches.length || Number(item.batchId) === Number(sourceBatchId)) continue;
                        if (matches.some(row => (row.normalized_payload[key].name || row.normalized_payload[key].full_name) !== payload.source_label)) {
                            throw new Error(`${item.name}: одинаковый ключ источника имеет другое имя. Проверьте сопоставление отдельно.`);
                        }
                        this.ensureContext();
                        await api.put(`/api/timesheets/imports/${item.batchId}/mappings`, payload, {ignoreModal: true});
                        await this.readItem(item);
                    }
                    this.mappingPending = false;
                } catch (error) { failure = error; throw error; }
            });
            if (failure) throw failure;
        },
        acceptDetails(payload) {
            this.objectPlan = null; this.selectedObjects = [];
            const item = this.items.find(row => Number(row.batchId) === Number(payload.batch && payload.batch.id));
            if (item) this.accept(item, payload);
        },
        clearQueue() {
            if (this.busy || this.locked) return;
            this.items = []; this.files = []; this.notice = ''; this.error = ''; this.checkpointDone = false; this.mappingPending = false;
            this.objectPlan = null; this.selectedObjects = [];
            this.persist(); this.$emit('clear');
        },
        journalData() {
            return {exported_at: new Date().toISOString(), company: (this.$store.getters.currentUser || {}).company,
                queue: {busy: this.busy, phase: this.phase, error: this.error, notice: this.notice, mapping_pending: this.mappingPending},
                months: this.items.map(item => ({file: item.name, period: item.period, batch_id: item.batchId,
                    state: item.state, message: item.message, record_counts: item.counts, totals_by_currency: item.totals,
                    diagnostics: item.diagnostics || null}))};
        },
        downloadJournal() {
            const journal = this.journalData();
            const url = URL.createObjectURL(new Blob([JSON.stringify(journal, null, 2)], {type: 'application/json'}));
            const link = document.createElement('a'); link.href = url; link.download = 'history-import-journal.json';
            link.click(); setTimeout(() => URL.revokeObjectURL(url), 1000);
        }
    }
};
</script>

<style scoped>
.import-queue { margin-top: 20px; padding: 16px; border: 1px solid #dee2e6; border-radius: 12px; background: #fff; }
.import-queue__actions { display: flex; flex-wrap: wrap; gap: 8px; }
.import-queue .table-responsive { position: relative; }
.import-queue__objects { padding: 12px; background: #f1f3f5; border-radius: 8px; }
.import-queue__objects details { max-height: 360px; overflow-y: auto; }
.import-queue__object { padding: 8px 0; overflow-wrap: anywhere; border-bottom: 1px solid #dee2e6; }
.import-queue td { vertical-align: top; }
.import-queue td:first-child { min-width: 130px; }
.import-queue td small { overflow-wrap: anywhere; }
</style>
